import React from 'react';
import { Box, Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { BhiDateTimePicker } from 'src/components/Inputs/BhiDateTimePicker';
import { RecurringPeriod } from 'src/modules/campaigns/types/RecurringSettings';
import { SectionProps } from '../../../registry/sectionRegistry';
import { RecurringTemplateSettings, TemplateFieldControl } from '../../../types';
import {
  normalizeRecurringTemplateSettings,
  validateRecurringTemplateSettings,
  REQUIRED_RECURRING_TEMPLATE_FIELDS,
} from '../../../helpers';
import { SettingsControl } from '../SettingsControl';
import { toDayjs, toIso } from '../dateValue';
import { daysOfWeekOptions, recurringPeriodOptions } from 'src/modules/campaigns/helpers';
import { CircleChip, noTopGutterSx, Panel } from 'src/UIKit';

/**
 * Template settings for recurring campaigns. Lets the admin set the default
 * value + visible/required for each runtime recurring field
 * (period, interval, days of week, date range).
 */
export const RecurringSettingsSection: React.FC<SectionProps> = ({ config, onConfigUpdate }) => {
  const settings = normalizeRecurringTemplateSettings(config.recurringSettings);
  const errors = validateRecurringTemplateSettings(settings);

  const updateControl = <K extends keyof RecurringTemplateSettings>(
    key: K,
    next: RecurringTemplateSettings[K],
  ) => {
    onConfigUpdate({ recurringSettings: { ...settings, [key]: next } });
  };

  const isLocked = (key: keyof RecurringTemplateSettings) =>
    REQUIRED_RECURRING_TEMPLATE_FIELDS.includes(key);

  return (
    <Grid item xs={12} sx={noTopGutterSx}>
      <Panel>
        <Typography variant="subtitle1" mb={2} fontWeight="fontWeightBold">
          Recurring Settings
        </Typography>

        <SettingsControl
          label="Period"
          control={settings.period}
          locked={isLocked('period')}
          onChange={next => updateControl('period', next as TemplateFieldControl<RecurringPeriod>)}
        >
          {(value, setValue) => (
            <Select
              size="small"
              fullWidth
              value={value}
              onChange={e => setValue(e.target.value as RecurringPeriod)}
              inputProps={{ 'aria-label': 'Period default' }}
            >
              {recurringPeriodOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          )}
        </SettingsControl>

        <SettingsControl label="Interval" control={settings.interval} locked={isLocked('interval')} onChange={next => updateControl('interval', next)}>
          {(value, setValue) => (
            <TextField
              type="number"
              fullWidth
              value={value}
              onChange={e => setValue(Number(e.target.value))}
              inputProps={{ 'aria-label': 'Interval default', min: 1 }}
            />
          )}
        </SettingsControl>

        <SettingsControl label="Days of Week" control={settings.daysOfWeek} locked={isLocked('daysOfWeek')} onChange={next => updateControl('daysOfWeek', next)}>
          {(value, setValue) => (
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" width="100%">
              {daysOfWeekOptions.map(option => {
                const checked = value.includes(option.value);
                return (
                  <CircleChip
                    key={option.value}
                    label={option.label}
                    title={option.name}
                    aria-label={option.name}
                    size="small"
                    variant={checked ? 'filled' : 'outlined'}
                    color={checked ? 'primary' : 'default'}
                    onClick={() =>
                      setValue(
                        checked
                          ? value.filter(d => d !== option.value)
                          : [...value, option.value].sort((a, b) => a - b),
                      )
                    }
                  />
                );
              })}
            </Box>
          )}
        </SettingsControl>

        <SettingsControl label="Start Date" control={settings.dateStart} locked={isLocked('dateStart')} onChange={next => updateControl('dateStart', next)}>
          {(value, setValue) => (
            <BhiDateTimePicker allowEmpty value={toDayjs(value)} onChange={next => setValue(toIso(next))} />
          )}
        </SettingsControl>

        <SettingsControl
          label="End Date"
          control={settings.dateEnd}
          onChange={next => updateControl('dateEnd', next)}
          error={errors['recurringSettings.dateEnd']}
        >
          {(value, setValue) => (
            <BhiDateTimePicker allowEmpty value={toDayjs(value)} onChange={next => setValue(toIso(next))} />
          )}
        </SettingsControl>
      </Panel>
    </Grid>
  );
};
